/**
 * Context and consumer hook for {@link CurrentUserProvider}.
 *
 * Lifted out of `useCurrentUser.tsx` to satisfy the
 * `react-refresh/only-export-components` rule (#2548): a `.tsx` file that
 * exports both a component and a hook breaks Fast Refresh because the
 * runtime cannot tell which export is the component to swap. The Provider
 * component stays in the `.tsx` file; the context + state type + consumer
 * hook live here.
 */
import { createContext, useContext } from 'react';

/** Whether the current authenticated user is an admin or a signer. */
export type UserType = 'admin' | 'signer';

/** Derived attributes for the current authenticated user. */
export interface CurrentUserState {
    /** Display name (given+family, or email fallback). */
    displayName: string | null;
    /** The raw email claim from the JWT, exposed separately for display in the user menu. */
    email: string | null;
    /** The resolved user type, or null while loading / on failure. */
    userType: UserType | null;
    /** Convenience flag: `true` when {@link userType} is `'admin'`. */
    isAdmin: boolean;
    /** `true` until the first auth-session read resolves. */
    loading: boolean;
}

/**
 * React context that holds the current authenticated user's state. `null`
 * means no provider is mounted above the consumer — {@link useCurrentUser}
 * throws in that case.
 */
export const CurrentUserContext = createContext<CurrentUserState | null>(null);

/**
 * Reads the current user state from `CurrentUserProvider`. Throws if used
 * outside a provider.
 */
export function useCurrentUser(): CurrentUserState {
    const context = useContext(CurrentUserContext);
    if (context === null) {
        throw new Error('useCurrentUser must be used within a CurrentUserProvider');
    }
    return context;
}
